/* eslint-disable camelcase */
import FileDownload from 'react-file-download';
import { toast } from 'react-toastify';
import qs from 'qs';
import { actions } from 'forms/rrf';
import ToastMessage from '../components/common/toast-message';
import {
  GET_SUBMISSION_LIST_PENDING,
  GET_SUBMISSION_LIST_REJECTED,
  GET_SUBMISSION_LIST_FETCHED,
  GET_USER_SUBMISSION_LIST_PENDING,
  GET_USER_SUBMISSION_LIST_REJECTED,
  GET_USER_SUBMISSION_LIST_FETCHED,
  GET_USER_SUBMISSION_OVERVIEW_PENDING,
  GET_USER_SUBMISSION_OVERVIEW_REJECTED,
  GET_USER_SUBMISSION_OVERVIEW_FETCHED,
  GET_SUBMISSION_PENDING,
  GET_SUBMISSION_REJECTED,
  GET_SUBMISSION_FETCHED,
  GET_SUBMISSION_META_PENDING,
  GET_SUBMISSION_META_REJECTED,
  GET_SUBMISSION_META_FULFILLED,
  GET_SELL_IT_OBJECTIVES_PENDING,
  GET_SELL_IT_OBJECTIVES_REJECTED,
  GET_SELL_IT_OBJECTIVES_FULFILLED,
  GET_NGP_OBJECTIVES_PENDING,
  GET_NGP_OBJECTIVES_REJECTED,
  GET_NGP_OBJECTIVES_FULFILLED,
  GET_NEXT_AUDIT_SUBMISSION_PENDING,
  GET_NEXT_AUDIT_SUBMISSION_FULFILLED,
  GET_NEXT_AUDIT_SUBMISSION_REJECTED
} from '../reducers/submissionsReducer';
import api from '../irelandApi';
import store, { history } from '../store';
import { destandard, objectToFormData } from '../utils/commonFunctions';

const SUBMISSIONS_URL = 'api/admin/submissions';
const TEAM_SUBMISSIONS_URL = 'api/team/submissions';

// Match the leading path segment (e.g. `/team`, `/team/...`) rather than a bare
// substring, so routes that merely contain the word (e.g. `/admin/teams`) don't match.
const isPathSegment = (pathname, segment) => new RegExp(`^/${segment}(/|$)`).test(pathname);

// The current path decides the scope: /team/* hits the team-scoped endpoints, everything else uses the admin ones.
const submissionsUrl = () => {
  const { pathname } = history.location;
  return isPathSegment(pathname, 'team') ? TEAM_SUBMISSIONS_URL : SUBMISSIONS_URL;
};

export const redirect = () => {
  const { pathname } = history.location;
  let path = 'admin';

  if (isPathSegment(pathname, 'team')) {
    path = 'team';
  }

  if (isPathSegment(pathname, 'admin/audit')) {
    path = 'admin/audit';
  }

  history.push(`/${path}/submissions`);
};

const redirectToNextSubmission = () => {
  history.push('/admin/audit/submissions/next');
};

const removeNullFields = (data) => {
  const copy = { ...data };

  Object.keys(copy).forEach((key) => {
    if (key === 'audit_completed' && copy[key] !== true) {
      copy[key] = copy[key] || 'false';
    } else {
      copy[key] = copy[key] || '';
    }
  });

  return copy;
};

const prepareSubmissionPayload = (payload) => {
  const submissionsData = removeNullFields(payload);

  if (submissionsData.incentive_id && submissionsData.status === 'APPROVED') {
    submissionsData.audit_completed = 'true';
  }

  const { audit_amounts, number_of_packs_solds } = submissionsData;
  submissionsData.submissions_packs_sold_attributes = [];

  if (number_of_packs_solds) {
    const numberOfPacksSolds = number_of_packs_solds.map(numberOfPacksSold => ({
      id: numberOfPacksSold.id,
      submission_id: submissionsData.id,
      brand_id: numberOfPacksSold['brand-id'],
      product_id: numberOfPacksSold['product-id'],
      number_of_packs_sold: numberOfPacksSold.value
    }));

    submissionsData.submissions_packs_sold_attributes.push(...numberOfPacksSolds);
    delete submissionsData.number_of_packs_solds;
  }

  if (audit_amounts) {
    audit_amounts.forEach((auditAmount) => {
      const hasProductId = auditAmount['product-id'] !== undefined && auditAmount['product-id'] !== null;
      const hasBrandId = auditAmount['brand-id'] !== undefined && auditAmount['brand-id'] !== null;

      let target = null;

      if (hasProductId) {
        target = submissionsData.submissions_packs_sold_attributes
          .find(item => item.product_id === auditAmount['product-id']);
      }

      if (!target && hasBrandId) {
        target = submissionsData.submissions_packs_sold_attributes
          .find(item => item.brand_id === auditAmount['brand-id']);
      }

      if (target) {
        target.audit_amount = auditAmount.value;
      }
    });

    delete submissionsData.audit_amounts;
  }

  submissionsData.further_validation_required = payload.further_validation_required;

  delete submissionsData.decline_reason;
  delete submissionsData.proof_of_purchases;
  delete submissionsData.week_of_sale;

  if (submissionsData.proof_of_purchases_attributes) {
    submissionsData.proof_of_purchases_attributes = Object.assign({}, ...submissionsData.proof_of_purchases_attributes);
  }

  return objectToFormData(submissionsData, 'submission');
};

export const getSubmissions = (
  outletCode,
  status,
  dateFrom,
  dateTo,
  auditCompleted,
  furtherValidationRequired,
  page = 1,
  submissionType,
  postcodeplanObjectiveId,
  auditorUser
) => (dispatch) => {
  dispatch({ type: GET_SUBMISSION_LIST_PENDING });

  const q = {
    outlet_code: outletCode,
    status,
    upload_date_from: dateFrom,
    upload_date_to: dateTo,
    audit_completed: auditCompleted,
    further_validation_required: furtherValidationRequired,
    submission_type: submissionType,
    postcodeplan_objective_id: postcodeplanObjectiveId,
    auditor_user: auditorUser
  };

  return api.get(`${submissionsUrl()}?${qs.stringify({ q }, { encodeValuesOnly: true })}&page=${page}`)
    .then((response) => {
      dispatch({
        type: GET_SUBMISSION_LIST_FETCHED,
        payload: response.data
      });
    })
    .catch((error) => {
      dispatch({
        type: GET_SUBMISSION_LIST_REJECTED,
        payload: error.response
      });
    });
};

export const getSubmissionsCsv = (
  outletCode,
  status,
  dateFrom,
  dateTo,
  auditCompleted,
  submissionType,
  postcodeplanObjectiveId,
  auditorUser
) => {
  const q = {
    outlet_code: outletCode,
    status,
    upload_date_from: dateFrom,
    upload_date_to: dateTo,
    audit_completed: auditCompleted,
    submission_type: submissionType,
    postcodeplan_objective_id: postcodeplanObjectiveId,
    auditor_user: auditorUser
  };

  return api.get(`${submissionsUrl()}.csv?${qs.stringify({ q }, { encodeValuesOnly: true })}`)
    .then((response) => {
      const filename = 'submissions.csv';

      FileDownload(response.data, filename);
    })
    .catch((error) => {
      toast.error(<ToastMessage iconStyle="error" message={`Submissions can not be downloaded: ${error.response.data.errors[0]}`}/>);
    });
};

export const getUserSubmissions = (
  outlet = null,
  perPage = null
) => (dispatch) => {
  dispatch({ type: GET_USER_SUBMISSION_LIST_PENDING });

  const params = perPage
    ? `?outlet=${outlet}&per_page=${perPage}`
    : `?outlet=${outlet}`;
  return api
    .get(`/api/submissions${params}`)
    .then((response) => {
      dispatch({
        type: GET_USER_SUBMISSION_LIST_FETCHED,
        payload: { data: response.data, outlet }
      });
    })
    .catch((error) => {
      dispatch({
        type: GET_USER_SUBMISSION_LIST_REJECTED,
        payload: error.response
      });
    });
};

export const getUserSubmissionsOverview = outlet => (dispatch) => {
  dispatch({ type: GET_USER_SUBMISSION_OVERVIEW_PENDING });
  return api
    .get(`/api/submissions/submissions_overview?outlet=${outlet}`)
    .then((response) => {
      dispatch({
        type: GET_USER_SUBMISSION_OVERVIEW_FETCHED,
        payload: { outlet, ...response.data }
      });
    })
    .catch((error) => {
      dispatch({
        type: GET_USER_SUBMISSION_OVERVIEW_REJECTED,
        payload: error.response
      });
    });
};

export const getSubmission = id => (dispatch) => {
  dispatch({type: GET_SUBMISSION_PENDING});

  return api.get(`${submissionsUrl()}/${id}`)
    .then((response) => {
      dispatch({
        type: GET_SUBMISSION_FETCHED,
        payload: response.data,
      });
    })
    .catch((error) => {
      dispatch({
        type: GET_SUBMISSION_REJECTED,
        payload: error.response
      });
    });
};

export const createSubmission = payload => api.post('api/submissions', prepareSubmissionPayload(destandard(payload)))
  .then(() => {
    toast.success(<ToastMessage iconStyle="success" message="Submission has been saved successfully"/>);
  })
  .catch((error) => {
    toast.error(<ToastMessage iconStyle="error" message={error.response.data.message} />);

    const errorData = error.response ? { ...error.response.data.errors } : {};

    return Promise.reject(errorData);
  });

export const getSubmissionMeta = () => (dispatch) => {
  dispatch({type: GET_SUBMISSION_META_PENDING});
  return api.get('api/submissions/get_meta')
    .then((response) => {
      dispatch({
        type: GET_SUBMISSION_META_FULFILLED,
        payload: response.data
      });
    })
    .catch((error) => {
      dispatch({
        type: GET_SUBMISSION_META_REJECTED,
        payload: error.response
      });
    });
};

export const getSellItObjectives = id => (dispatch) => {
  dispatch({type: GET_SELL_IT_OBJECTIVES_PENDING});
  return api.get(`api/outlets/${id}/sell_it_objectives`)
    .then((response) => {
      dispatch({
        type: GET_SELL_IT_OBJECTIVES_FULFILLED,
        payload: response.data
      });
    })
    .catch((error) => {
      dispatch({
        type: GET_SELL_IT_OBJECTIVES_REJECTED,
        payload: error.response
      });
    });
};

export const getNgpObjectives = id => (dispatch) => {
  dispatch({type: GET_NGP_OBJECTIVES_PENDING});
  return api.get(`api/outlets/${id}/ngp_objectives`)
    .then((response) => {
      dispatch({
        type: GET_NGP_OBJECTIVES_FULFILLED,
        payload: response.data
      });
    })
    .catch((error) => {
      dispatch({
        type: GET_NGP_OBJECTIVES_REJECTED,
        payload: error.response
      });
    });
};

export const validateSubmission = (id, payload) =>
  api.patch(`${submissionsUrl()}/${id}/validate_submission`, prepareSubmissionPayload(destandard({ ...payload, id })))
    .then(() => {
      toast.success(<ToastMessage iconStyle="success" message="Submission has been saved successfully"/>);
    })
    .catch((error) => {
      if (error.response && error.response.data && error.response.data.errors) {
        const errors = Object.keys(error.response.data.errors).map(key => error.response.data.errors[key]).join(', ');
        toast.error(<ToastMessage iconStyle="error" message={errors}/>);
      } else {
        toast.error(<ToastMessage iconStyle="error" message="Error while saving submission"/>);
      }

      const errorData = error.response ? error.response.data.errors : [];

      return Promise.reject(errorData);
    })
    .then(() => {
      redirect();
    });

export const declineSubmission = (id, payload, validated = false) => {
  let url;
  let body;
  if (validated) {
    url = `${submissionsUrl()}/${id}/decline_validated_submission`;
    body = { decline_reason: payload.decline_reason };
  } else {
    url = `${submissionsUrl()}/${id}/decline_submission`;
    body = prepareSubmissionPayload(destandard({ ...payload, id }));
  }

  return api.patch(url, body)
    .then(() => {
      toast.success(<ToastMessage iconStyle="success" message="Submission has been saved successfully"/>);
    })
    .catch((error) => {
      toast.error(<ToastMessage iconStyle="error" message="Error while saving submission"/>);

      const errorData = error.response ? error.response.data.errors : [];

      return Promise.reject(errorData);
    })
    .then(() => {
      if (history.location.pathname.startsWith('/admin/audit/submissions/')) {
        redirectToNextSubmission();
      } else {
        redirect();
      }
    });
};

export const updateSubmission = (id, payload) =>
  api.patch(`${submissionsUrl()}/${id}`, prepareSubmissionPayload(destandard({ ...payload, id })))
    .then(() => {
      toast.success(<ToastMessage iconStyle="success" message="Submission has been saved successfully"/>);
    })
    .catch((error) => {
      toast.error(<ToastMessage iconStyle="error" message="Error while saving submission"/>);

      const errorData = error.response ? error.response.data.errors : [];

      return Promise.reject(errorData);
    })
    .then(() => {
      redirect();
    });

export const updateSubmissionFurtherValidationRequired = (id, payload) => {
  const submissionsData = removeNullFields(destandard({ ...payload, id }));

  submissionsData.further_validation_required = payload.further_validation_required;

  return api.patch(`${SUBMISSIONS_URL}/${id}/further_validation_required`, objectToFormData(submissionsData, 'submission'))
    .then(() => {
      toast.success(<ToastMessage iconStyle="success" message="Submission has been saved successfully"/>);
    })
    .catch((error) => {
      toast.error(<ToastMessage iconStyle="error" message="Error while saving submission"/>);

      const errorData = error.response ? error.response.data.errors : [];

      return Promise.reject(errorData);
    });
};

export const internalEnquirySubmission = (id, payload) =>
  api.patch(`${SUBMISSIONS_URL}/${id}/internal_enquiry`, prepareSubmissionPayload(destandard({ ...payload, id })))
    .then(() => {
      toast.success(<ToastMessage iconStyle="success" message="Submission status has been updated successfully"/>);

      redirectToNextSubmission();
    })
    .catch((error) => {
      toast.error(<ToastMessage iconStyle="error" message="Error while saving submission status"/>);

      const errorData = error.response ? error.response.data.errors : [];

      return Promise.reject(errorData);
    });

export const repEnquirySubmission = (id, payload) =>
  api.patch(`${SUBMISSIONS_URL}/${id}/rep_enquiry`, prepareSubmissionPayload(destandard({ ...payload, id })))
    .then(() => {
      toast.success(<ToastMessage iconStyle="success" message="Submission status has been updated successfully"/>);

      redirectToNextSubmission();
    })
    .catch((error) => {
      toast.error(<ToastMessage iconStyle="error" message="Error while saving submission status"/>);

      const errorData = error.response ? error.response.data.errors : [];

      return Promise.reject(errorData);
    });

export const itukEnquirySubmission = (id, payload) =>
  api.patch(`${SUBMISSIONS_URL}/${id}/ituk_enquiry`, prepareSubmissionPayload(destandard({ ...payload, id })))
    .then(() => {
      toast.success(<ToastMessage iconStyle="success" message="Submission status has been updated successfully"/>);

      redirectToNextSubmission();
    })
    .catch((error) => {
      toast.error(<ToastMessage iconStyle="error" message="Error while saving submission status"/>);

      const errorData = error.response ? error.response.data.errors : [];

      return Promise.reject(errorData);
    });

export const auditorValidateSubmission = (id, payload) =>
  api.patch(`${SUBMISSIONS_URL}/${id}/auditor_validate`, prepareSubmissionPayload(destandard({ ...payload, id })))
    .then(() => {
      toast.success(<ToastMessage iconStyle="success" message="Submission has been validated successfully"/>);

      redirectToNextSubmission();
    })
    .catch((error) => {
      if (error.response && error.response.data && error.response.data.errors) {
        const errors = Object.keys(error.response.data.errors).map(key => error.response.data.errors[key]).join(', ');
        toast.error(<ToastMessage iconStyle="error" message={errors}/>);
      } else {
        toast.error(<ToastMessage iconStyle="error" message="Error while saving submission status"/>);
      }

      const errorData = error.response ? error.response.data.errors : [];

      return Promise.reject(errorData);
    });

export const auditorHoldSubmission = (id, payload) =>
  api.patch(`${SUBMISSIONS_URL}/${id}/hold`, prepareSubmissionPayload(destandard({ ...payload, id })))
    .then(() => {
      toast.success(<ToastMessage iconStyle="success" message="Submission has been holded successfully"/>);

      redirectToNextSubmission();
    })
    .catch((error) => {
      toast.error(<ToastMessage iconStyle="error" message="Error while saving submission status"/>);
      const errorData = error.response ? error.response.data.errors : [];

      return Promise.reject(errorData);
    });

export const getNextAuditSubmission = () => (dispatch) => {
  dispatch({ type: GET_NEXT_AUDIT_SUBMISSION_PENDING });
  store.dispatch(actions.reset('deep.admin_submission'));
  api.get(`${SUBMISSIONS_URL}/next_audit_submission`)
    .then((response) => {
      dispatch({
        type: GET_NEXT_AUDIT_SUBMISSION_FULFILLED,
        payload: response.data
      });
    })
    .catch((error) => {
      dispatch({
        type: GET_NEXT_AUDIT_SUBMISSION_REJECTED,
        payload: error.response
      });
    });
};
